import { createApp } from "vue";
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faArrowDown, faPhotoFilm } from "@fortawesome/free-solid-svg-icons";
import { faArrowUp } from "@fortawesome/free-solid-svg-icons";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import { faBullhorn } from "@fortawesome/free-solid-svg-icons";
import { faBookOpen } from "@fortawesome/free-solid-svg-icons";
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import { faChalkboard } from "@fortawesome/free-solid-svg-icons";
import { faCamera } from "@fortawesome/free-solid-svg-icons";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { faCirclePlay } from "@fortawesome/free-solid-svg-icons";
import { faClipboard } from "@fortawesome/free-solid-svg-icons";
import { faClipboardUser } from "@fortawesome/free-solid-svg-icons";
import { faComment } from "@fortawesome/free-solid-svg-icons";
import { faComments } from "@fortawesome/free-solid-svg-icons";
import { faEllipsis } from "@fortawesome/free-solid-svg-icons";
import { faFile } from "@fortawesome/free-solid-svg-icons";
import { faHeart } from "@fortawesome/free-solid-svg-icons";
import { faHouse } from "@fortawesome/free-solid-svg-icons";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { faPencil } from "@fortawesome/free-solid-svg-icons";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { faReply } from "@fortawesome/free-solid-svg-icons";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { faThumbsUp } from "@fortawesome/free-solid-svg-icons";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import { faUsers } from "@fortawesome/free-solid-svg-icons";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { faCheckToSlot } from "@fortawesome/free-solid-svg-icons";
import { faChartPie } from "@fortawesome/free-solid-svg-icons";
import { faDesktop } from "@fortawesome/free-solid-svg-icons";
import { faFilePdf } from "@fortawesome/free-solid-svg-icons";
import { faImage } from "@fortawesome/free-solid-svg-icons";
import { faFilm } from "@fortawesome/free-solid-svg-icons";
import { faVolumeUp } from "@fortawesome/free-solid-svg-icons";
import { faToolbox } from "@fortawesome/free-solid-svg-icons";

import {
  addRequestHandler,
  addResponseHandler,
  type RequestHandlerParams,
  setGlobalRequestHeader,
} from "@virgodev/bazaar/functions/api";
import Cookies from "js-cookie";

import App from "./App.vue";
import router from "./router";
import { useStore } from "./store";
import { createPinia } from "pinia";
import BaseAlert from "./components/BaseAlert.vue";
import BaseButton from "./components/BaseButton.vue";
import BaseSpinner from "./components/BaseSpinner.vue";
import TransitionSlide from "./components/TransitionSlide.vue";
import Vue3EasyDataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";
import Multiselect from "@vueform/multiselect";

library.add(faArrowDown);
library.add(faArrowUp);
library.add(faBell);
library.add(faBullhorn);
library.add(faBookOpen);
library.add(faCalendar);
library.add(faChalkboard);
library.add(faCamera);
library.add(faCheck);
library.add(faChevronDown);
library.add(faChevronRight);
library.add(faCircleExclamation);
library.add(faCirclePlay);
library.add(faClipboard);
library.add(faClipboardUser);
library.add(faComment);
library.add(faComments);
library.add(faDesktop);
library.add(faEllipsis);
library.add(faFile);
library.add(faHeart);
library.add(faHouse);
library.add(faMagnifyingGlass);
library.add(faPaperPlane);
library.add(faPencil);
library.add(faPlus);
library.add(faReply);
library.add(faTrash);
library.add(faTriangleExclamation);
library.add(faUser);
library.add(faUsers);
library.add(faXmark);
library.add(faExclamationTriangle);
library.add(faCheckToSlot);
library.add(faChartPie);
library.add(faThumbsUp);
library.add(faPhotoFilm);
library.add(faFilePdf);
library.add(faImage);
library.add(faFilm);
library.add(faVolumeUp);
library.add(faToolbox);

if (process.env.NODE_ENV === "development") {
  addRequestHandler(({ options }: RequestHandlerParams) => {
    options.credentials = "include";
  });
}

addResponseHandler("all", () => {
  setGlobalRequestHeader("X-CSRFToken", Cookies.get("csrftoken"));
});

addResponseHandler(403, () => {
  useStore().logOut();

  if (router.currentRoute.value.name !== "login") {
    router.push({
      name: "login",
      query: { next: router.currentRoute.value.fullPath },
    });
  }
});

const app = createApp(App);
app.component("FontAwesomeIcon", FontAwesomeIcon);
app.component("BaseAlert", BaseAlert);
app.component("BaseButton", BaseButton);
app.component("BaseSpinner", BaseSpinner);
app.component("TransitionSlide", TransitionSlide);
app.component("VueEasyTable", Vue3EasyDataTable);
app.component("MultiSelect", Multiselect);
app.use(createPinia());
app.use(router).mount("#app");

declare module "@vue/runtime-core" {
  interface GlobalComponents {
    BaseAlert: typeof BaseAlert;
    BaseButton: typeof BaseButton;
    BaseSpinner: typeof BaseSpinner;
    TransitionSlide: typeof TransitionSlide;
    RouterLink: typeof import("vue-router")["RouterLink"];
    RouterView: typeof import("vue-router")["RouterView"];
  }
}
